import { useEffect, useRef, useState } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { type MapSettings } from '../lib/api'
import { addBasemap, mapSettings } from '../lib/basemap'

/** A point on a map that the dispatcher can move.
 *
 *  WHAT IT IS FOR
 *
 *  The customer, on the intake box: "before hitting the broadcast button, I
 *  should be able to see a preview of the map where there is and to discuss it
 *  with the caller to make sure that we are on the same page." So this is
 *  shown WHILE the caller is still on the phone, and the whole value of it is
 *  that the two of them settle the spot before anybody is sent.
 *
 *  A DRAGGED PIN IS NOT A CORRECTION TO THE ADDRESS
 *
 *  It belongs to THIS call — the loading bay behind the building, the far side
 *  of the bridge, the shoulder rather than the junction. `places` holds what
 *  the geocoder found for the address itself, shared by every call ever taken
 *  there, and a dispatcher's pin must not rewrite it for the next one.
 *  Migration 053 puts the moved pin on `incident_locations` with who moved it.
 *
 *  AND IT IS STILL NEVER A GUESS
 *
 *  Nothing here invents a coordinate. With none, this renders a sentence
 *  saying the map does not know the address — the state where a dispatcher
 *  asks for a cross street — and never an empty grey square, which reads as
 *  "still loading".
 *
 *  `PlaceMap` is the other one, for an address that already has a place row.
 *  They are deliberately separate: that one can ask the server to geocode and
 *  writes the answer onto the place; this one is handed coordinates and hands
 *  back wherever the pin was dropped. */

const PIN = L.divIcon({
  className: 'place-pin',
  html:
    '<svg viewBox="0 0 24 32" width="26" height="34" aria-hidden="true">' +
    '<path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 20 12 20s12-11 12-20c0-6.6-5.4-12-12-12z" ' +
    'fill="#d33a3a" stroke="#fff" stroke-width="2"/>' +
    '<circle cx="12" cy="12" r="4.5" fill="#fff"/></svg>',
  iconSize: [26, 34],
  iconAnchor: [13, 34],
})

export function PinMap({
  latitude, longitude, height = 190, zoom = 17, clickToPlace = false, onMoved,
}: {
  latitude: number | null
  longitude: number | null
  height?: number
  /** 17 for a building; wider for a pin put by hand from the service area
   *  (071), where the dispatcher has the whole area to find the spot in. */
  zoom?: number
  /** A click on the map puts the pin there (071) -- only where asked: on a
   *  building's own map a stray click must not move the call. */
  clickToPlace?: boolean
  /** Where the dispatcher dropped it. Called on drag end only — panning and
   *  zooming move the MAP, not the claim about where the call is. */
  onMoved?: (latitude: number, longitude: number) => void
}) {
  const box = useRef<HTMLDivElement | null>(null)
  const map = useRef<L.Map | null>(null)
  const pin = useRef<L.Marker | null>(null)
  const detach = useRef<(() => void) | null>(null)
  const moved = useRef(onMoved)
  const [settings, setSettings] = useState<MapSettings | null>(null)
  const [note, setNote] = useState<string | null>(null)

  // THE HANDLER IS READ THROUGH A REF, so a re-render of the form does not
  // have to tear the map down and build it again -- which loses the zoom the
  // dispatcher just set, mid-call, every time he types a letter elsewhere.
  moved.current = onMoved
  const clicks = useRef(clickToPlace)
  clicks.current = clickToPlace
  /** The zoom last asked for, so a change of it -- a hand-placed pin becoming
   *  a found one -- zooms, while a re-render keeps his own zoom. */
  const askedZoom = useRef(zoom)

  useEffect(() => {
    let alive = true
    mapSettings().then((s) => { if (alive) setSettings(s) })
    return () => { alive = false }
  }, [])

  useEffect(() => {
    if (!settings?.enabled || !settings.tile_url) return
    if (latitude === null || longitude === null) return
    if (!box.current) return

    if (!map.current) {
      map.current = L.map(box.current, {
        zoomControl: true, attributionControl: true, scrollWheelZoom: false,
      }).setView([latitude, longitude], zoom)
      // OpenStreetMap or Google, by the organization's map level; either way
      // the pin below is a Leaflet marker and drags the same. lib/basemap.ts.
      detach.current = addBasemap(map.current, settings, setNote)
      pin.current = L.marker([latitude, longitude], { icon: PIN, draggable: true })
        .addTo(map.current)
      pin.current.on('dragend', () => {
        const at = pin.current?.getLatLng()
        if (at) moved.current?.(at.lat, at.lng)
      })
      // A CLICK PUTS THE PIN THERE (071): quicker than a drag across a whole
      // service area, and the same claim as one.
      map.current.on('click', (e: L.LeafletMouseEvent) => {
        if (!clicks.current) return
        pin.current?.setLatLng(e.latlng)
        moved.current?.(e.latlng.lat, e.latlng.lng)
      })
    } else {
      // A NEW ADDRESS MOVES THE PIN AND THE VIEW; the dispatcher picked a
      // different suggestion and is looking at a different building.
      pin.current?.setLatLng([latitude, longitude])
      const z = askedZoom.current !== zoom ? zoom : map.current.getZoom()
      map.current.setView([latitude, longitude], z)
    }
    askedZoom.current = zoom
  }, [settings, latitude, longitude, zoom])

  useEffect(() => () => {
    detach.current?.()
    detach.current = null
    map.current?.remove()
    map.current = null
    pin.current = null
  }, [])

  if (settings && !settings.enabled) {
    return <p className="mapoff">No map is switched on for this customer.</p>
  }
  if (latitude === null || longitude === null) {
    return null
  }
  return (
    <>
      <div className="pinmap" style={{ height }}>
        <div ref={box} style={{ height: '100%' }} />
      </div>
      {/* Why this is not the Google map the level promises. Outside the box,
          which clips, and only ever one line. */}
      {note && <p className="mapoff">{note}</p>}
    </>
  )
}
